import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Card, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { TruxTableCell } from 'src/components/trux-table-cell';
import { formatTimeAsHourMin } from 'src/utils/date-utils';
import { RouteBuddySimulateResponse, SimSegment, STOP_LABELS, StopLabel, TripStop } from './types';

type TimelineEntry = {
  time: string;
  icon: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'grey';
  title: string;
  detail?: string;
  chip?: { text: string; background: string };
};

const SEGMENT_ENTRIES: Record<string, { icon: string; color: TimelineEntry['color']; title: string }> = {
  DRIVE: { icon: 'glyphs-poly:truck', color: 'primary', title: 'Drive' },
  BREAK_30M: { icon: 'fluent-emoji-flat:cup-with-straw', color: 'warning', title: '30 min break' },
  REST_10H: { icon: 'glyphs-poly:bed', color: 'info', title: '10 hr rest' },
  RESTART_34H: { icon: 'glyphs-poly:garage', color: 'error', title: '34 hr restart' },
  WAIT: { icon: 'mdi:clock-outline', color: 'grey', title: 'Wait' },
  DWELL: { icon: 'mdi:forklift', color: 'success', title: 'At the dock' },
};

function stopName(stops: TripStop[], stopIndex: number, label: string) {
  const stop = stops[stopIndex];
  const typeName = STOP_LABELS[label as StopLabel] || label;
  const address = stop?.location.address ? ` · ${stop.location.address}` : '';
  return `${typeName}${address}`;
}

function segmentDetail(segment: SimSegment) {
  const duration = formatTimeAsHourMin(segment.hours);
  if (segment.type === 'DRIVE') {
    return `${duration} · ${segment.miles.toFixed(0)} mi`;
  }
  return duration;
}

function buildEntries(simulation: RouteBuddySimulateResponse, stops: TripStop[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  if (simulation.segments.length > 0) {
    entries.push({
      time: simulation.segments[0].from,
      icon: 'mdi:map-marker',
      color: 'primary',
      title: 'Departs',
    });
  }

  // stop arrivals happen exactly between segments, keyed by arrival time
  const arrivals = new Map<string, TimelineEntry>();
  simulation.stopResults.forEach((stopResult) => {
    let chip;
    if (stopResult.canMakeAppointment !== null && stopResult.slackHours !== null) {
      chip = stopResult.canMakeAppointment
        ? { text: `${formatTimeAsHourMin(stopResult.slackHours)} early`, background: '#36b746' }
        : { text: `${formatTimeAsHourMin(-stopResult.slackHours)} late`, background: '#ff5631' };
    }
    arrivals.set(stopResult.arrival, {
      time: stopResult.arrival,
      icon: stopResult.label === 'PICKUP' ? 'glyphs-poly:box-1' : 'glyphs-poly:flag',
      color: 'success',
      title: `Arrives · ${stopName(stops, stopResult.stopIndex, stopResult.label)}`,
      chip,
    });
  });

  simulation.segments.forEach((segment) => {
    const arrival = arrivals.get(segment.from);
    if (arrival) {
      entries.push(arrival);
      arrivals.delete(segment.from);
    }
    const config = SEGMENT_ENTRIES[segment.type] || SEGMENT_ENTRIES.WAIT;
    entries.push({
      time: segment.from,
      icon: config.icon,
      color: config.color,
      title: segment.type === 'WAIT' && segment.comment ? `Wait, ${segment.comment}` : config.title,
      detail: segmentDetail(segment),
    });
  });

  // arrivals at the very end of the trip have no segment after them
  arrivals.forEach((arrival) => entries.push(arrival));

  const lastStop = simulation.stopResults[simulation.stopResults.length - 1];
  if (lastStop) {
    entries.push({
      time: lastStop.departure,
      icon: 'mdi:check-circle-outline',
      color: 'success',
      title: 'Plan complete',
    });
  }

  return entries;
}

export default function TripTimeline({
  simulation,
  stops,
}: {
  simulation: RouteBuddySimulateResponse;
  stops: TripStop[];
}) {
  const { timezone } = simulation;
  const entries = buildEntries(simulation, stops);

  return (
    <Card sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
        Trip plan
      </Typography>

      <Timeline position="right" sx={{ p: 2 }}>
        {entries.map((entry, index) => (
          <TimelineItem key={index}>
            <TimelineOppositeContent sx={{ flex: 0.22, minWidth: 130 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {DateTime.fromISO(entry.time).setZone(timezone).toFormat('ccc h:mm a')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {DateTime.fromISO(entry.time).setZone(timezone).toFormat('LLL d')}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot color={entry.color === 'grey' ? undefined : entry.color} variant="outlined">
                <Iconify icon={entry.icon} width={18} />
              </TimelineDot>
              {index < entries.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 3 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">{entry.title}</Typography>
                {entry.chip && (
                  <Label sx={{ color: 'white', background: entry.chip.background }}>{entry.chip.text}</Label>
                )}
              </Stack>
              {entry.detail && (
                <Typography variant="caption" color="text.secondary">
                  {entry.detail}
                </Typography>
              )}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      <Typography variant="h6" sx={{ px: 2 }}>
        Day by day
      </Typography>
      <TableContainer sx={{ position: 'relative', overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TruxTableCell>Date</TruxTableCell>
              <TruxTableCell align="right">Driving</TruxTableCell>
              <TruxTableCell align="right">On Duty</TruxTableCell>
              <TruxTableCell align="right">Rest</TruxTableCell>
              <TruxTableCell align="right">Miles</TruxTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {simulation.days.map((day) => (
              <TableRow key={day.date}>
                <TruxTableCell>{DateTime.fromISO(day.date).toFormat('ccc LLL d')}</TruxTableCell>
                <TruxTableCell align="right">{formatTimeAsHourMin(day.driveHours)}</TruxTableCell>
                <TruxTableCell align="right">{formatTimeAsHourMin(day.onDutyHours)}</TruxTableCell>
                <TruxTableCell align="right">{formatTimeAsHourMin(day.restHours)}</TruxTableCell>
                <TruxTableCell align="right">{day.miles.toFixed(0)}</TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
